/**
 * The world-event registry: how Effects (render/effects.ts) answers each `WorldEvent` type lives in
 * a file of its own family in this folder (`combat.ts`, `movement.ts`, `progress.ts`, …), which
 * calls `registerFxEvent` when it loads. Effects loads every file here and `handle()` dispatches to
 * the one handler of the event's type, after the shared hooks it runs for every event.
 *
 * Adding a reaction is one call: `registerFxEvent('myEvent', (host, ev, w) => { … })`, `ev`
 * narrowed to that type. One handler per type; a second registration throws at load.
 */
import type { World, WorldEvent } from '@nine/shared';
import type { Effects } from '../../effects';

export type WorldEventType = WorldEvent['type'];
export type EventOf<T extends WorldEventType> = Extract<WorldEvent, { type: T }>;
export type FxEventHandler<T extends WorldEventType> = (host: Effects, ev: EventOf<T>, w: World) => void;

const handlers = new Map<WorldEventType, FxEventHandler<WorldEventType>>();

export function registerFxEvent<T extends WorldEventType>(
  types: T | readonly T[],
  fn: FxEventHandler<T>,
): void {
  for (const t of typeof types === 'string' ? [types] : types) {
    if (handlers.has(t)) throw new Error(`fx event '${t}' registered twice`);
    handlers.set(t, fn as unknown as FxEventHandler<WorldEventType>);
  }
}

/** Runs the handler registered for `ev.type`, if any. */
export function dispatchFxEvent(host: Effects, ev: WorldEvent, w: World): void {
  handlers.get(ev.type)?.(host, ev, w);
}
